(ns app.front.view.user-management.components
  "Components of the tenant and group user management views. They take the
  users and groups as data, keep their state in `app.front.view.user-management.state`
  under the caller's scope, and dispatch the caller's events to save, so the
  same editor serves the system admin's tenant commands and the group admin's
  `:group/*` commands."
  (:require [app.front.ui :as ui]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.select :as ui-select]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.util :refer [for-frag]]
            [app.front.view.user-management.model :as model]
            [app.front.view.user-management.state :as state]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [re-frame.core-instrumented :as rf]))

;; ---------------------------------------------------------------------------
;; Group choices
;; ---------------------------------------------------------------------------

(defn checkbox-block
  "A titled grid of user-group checkboxes. `on-toggle` gets the group id and
  the new checked state; `disabled?` the group."
  [{:keys [id-prefix title help groups selected on-toggle disabled?]}]
  (when (seq groups)
    [:div.mt-4
     (when title [:h5.mb-1 title])
     (when help [:p.text-sm.text-gray-600.mb-2 help])
     [:div.grid.gap-x-4 {:class "sm:grid-cols-2"}
      (for-frag [{:group/keys [name] :keys [db/id] :as group} groups]
        [:div
         (cc/checkbox {:id         (str id-prefix "-" id)
                       :label-text name
                       :checked    (contains? selected id)
                       :disabled   (boolean (and disabled? (disabled? group)))
                       :on-change  #(on-toggle id %)})])]]))

(defn role-select
  "One `<select>` for the roles of an external module: a user holds one at a
  time. \"No role\" leaves the current one without joining another."
  [{:keys [tr id-prefix title help groups selected on-toggle]}]
  (when (seq groups)
    (let [current (->> groups (filter (comp selected :db/id)) first)]
      [:div.mt-4
       [:h5.mb-1 title]
       (when help [:p.text-sm.text-gray-600.mb-2 help])
       [:div.grid.gap-x-4 {:class "sm:grid-cols-2"}
        [ui-select/select {:id         (str id-prefix "-select")
                           :tr         tr
                           :label-text title
                           :hide-label true
                           :values     groups
                           :val->str   :group/name
                           :empty-text (tr [:admin/tenant-external-role-none])
                           :value      current
                           :on-change  #(if %
                                          (on-toggle (:db/id %) true)
                                          (on-toggle (:db/id current) false))}]]])))

(defn group-checkboxes
  "A group's user-groups in their blocks: permissions, viewer access and groups
  for a Cloudpermit group; roles and departments for an external module, named
  `module-name`."
  [{:keys [tr id-prefix module-name groups selected on-toggle disabled?]}]
  (let [{:keys [permissions viewer plain external-roles external-departments]} (model/classify-groups groups)
        block                                                                  (fn [suffix props]
                                                                                 [checkbox-block
                                                                                  (merge {:id-prefix (str id-prefix suffix)
                                                                                          :selected  selected
                                                                                          :on-toggle on-toggle
                                                                                          :disabled? disabled?}
                                                                                         props)])]
    [:<>
     (block "-perm" {:title (tr [:admin/tenant-permissions]) :groups permissions})
     (block "-viewer" {:title  (tr [:admin/tenant-viewer-access])
                       :help   (tr [:admin/tenant-viewer-access-help])
                       :groups viewer})
     (block "-groups" {:title (tr [:admin/tenant-groups]) :groups plain})
     [role-select {:tr        tr
                   :id-prefix (str id-prefix "-role")
                   :title     (tr [:admin/tenant-external-roles] [module-name])
                   :help      (tr [:admin/tenant-external-roles-help])
                   :groups    external-roles
                   :selected  selected
                   :on-toggle on-toggle}]
     (block "-dept" {:title  (tr [:admin/tenant-external-departments] [module-name])
                     :groups external-departments})]))

(defn group-pills [names]
  [:div.flex.flex-wrap.items-center.-m-1
   ;; White reads against the table row's grey, grey against an expanded row's white.
   (for-frag [n (sort names)]
     [ui/tag {:type :round :color :white :class-name "[[aria-expanded=true]_&]:bg-gray-200"} n])])

;; ---------------------------------------------------------------------------
;; Row editor
;; ---------------------------------------------------------------------------

(defn user-editor
  "Edits one user's names and their memberships among `editable-ids`, the
  user-groups this view manages; Save sends only what changed, Cancel drops
  the changes.

  Props:
  - `:scope`, `:user`, `:id-prefix`
  - `:editable-ids` the user-group ids the draft covers
  - `:toggle-fn` `(fn [group-ids group-id checked?])`, the draft's group ids
    after a toggle (see `model/toggle-group`)
  - `:groups-editor` `(fn [group-ids on-toggle])`, the group choices
  - `:names?` whether the user has names to edit (not a service account)
  - `:save-event` gets the user id and `model/changes` appended
  - `:confirm-save` optional `(fn [changes])`, a confirmation text for Save
  - `:remove-button`, `:extra` hiccup"
  [{:keys [tr scope user id-prefix editable-ids toggle-fn groups-editor names?
           save-event confirm-save remove-button extra]}]
  (let [user-id  (:db/id user)
        original (model/draft-of user editable-ids)
        stored   (<sub [::state/draft scope user-id])
        draft    (or stored original)
        update!  (fn [f & args]
                   (rf/dispatch-sync (into [::state/update-draft scope user-id original f] args)))
        changes  (model/changes original draft)
        valid?   (and (seq (:group-ids draft))
                      (or (not names?)
                          (not-any? str/blank? [(:first-name draft) (:last-name draft)])))
        save     (fn [] (>evt (conj save-event user-id changes)))
        confirm  (when confirm-save (confirm-save changes))]
    [:div.p-2
     (when names?
       [:div.grid.gap-4 {:class "sm:grid-cols-3"}
        [ui/text-input {:id         (str id-prefix "-first-name-" user-id)
                        :label-text (tr [:user/first-name])
                        :value      (:first-name draft)
                        :invalid    (str/blank? (:first-name draft))
                        :on-change  #(update! assoc :first-name (-> % .-target .-value))}]
        [ui/text-input {:id         (str id-prefix "-last-name-" user-id)
                        :label-text (tr [:user/last-name])
                        :value      (:last-name draft)
                        :invalid    (str/blank? (:last-name draft))
                        :on-change  #(update! assoc :last-name (-> % .-target .-value))}]
        [ui/text-input {:id          (str id-prefix "-email-" user-id)
                        :label-text  (tr [:user/email])
                        :value       (or (:user/email user) (:user/username user))
                        :disabled    true
                        :helper-text (tr [:admin/tenant-email-immutable])}]])
     (groups-editor (:group-ids draft)
                    (fn [group-id checked?]
                      (update! update :group-ids toggle-fn group-id checked?)))
     extra
     [:div.flex.justify-between.items-center.mt-6.flex-wrap.gap-2
      remove-button
      [:div.flex.gap-2.ml-auto
       [ui/button {:kind     :secondary
                   :size     :small
                   :disabled (not stored)
                   :on-click #(>evt [::state/discard-draft scope user-id])}
        (tr [:cancel])]
       (if (and confirm valid? (model/dirty? changes))
         [ui/modal-confirmation-button
          {:tr                  tr
           :modal-title         (tr [:save])
           :trigger-button-text (tr [:save])
           :trigger-button-opts {:size :small :kind :primary}
           :on-yes-click        save}
          confirm]
         [ui/button {:kind     :primary
                     :size     :small
                     :disabled (not (and valid? (model/dirty? changes)))
                     :on-click save}
          (tr [:save])])]]]))

;; ---------------------------------------------------------------------------
;; Tables
;; ---------------------------------------------------------------------------

(defn persons-table
  "People with their names, emails and `group-names` (a fn of the user), rows
  expanding into `expanded-func`. `extra-cols` go after the groups."
  [{:keys [tr id users group-names expanded-func extra-cols]}]
  [sortable-table/table
   {:tr              tr
    :id              id
    :items           users
    :id-func         :db/id
    :initial-sorting [::display-name :asc]
    :expanded-func   expanded-func
    :cols            (into [[:user-management/name
                             (fn [user] [:span.font-medium (model/display-name user)])
                             {:sorting  (fn [a b] (compare (model/display-name a) (model/display-name b)))
                              :sort-key ::display-name}]
                            [:user/email
                             (fn [user] (or (:user/email user) (:user/username user)))
                             {:sorting true :truncate? true}]
                            [:admin/tenant-groups (comp group-pills group-names) {:flex 2}]]
                           extra-cols)}])

(defn service-accounts-table
  [{:keys [tr id users group-names expanded-func]}]
  [sortable-table/table
   {:tr            tr
    :id            id
    :items         users
    :id-func       :db/id
    :expanded-func expanded-func
    :cols          [[:admin/tenant-service-account-username :user/username {:sorting true}]
                    [:admin/tenant-groups (comp group-pills group-names) {:flex 2}]
                    [:admin/tenant-api-keys
                     (fn [user] (str (count (:user/api-keys user))))
                     {:width "120px"}]]}])

;; ---------------------------------------------------------------------------
;; Add user / add service account
;; ---------------------------------------------------------------------------

(defn add-button
  "Opens the scope's add form of `kind`, `:person` or `:service-account`, for
  an optional `target` when a view has several (e.g. one per group)."
  [{:keys [scope kind target label]}]
  [ui/button {:kind     :primary
              :size     :small
              :icon     i/plus
              :on-click #(>evt [::state/open-add-form scope {:kind kind :target target}])}
   label])

(defn add-modal
  "The scope's add form of `kind` and `target` (see `add-button`), submitting
  `submit-event` with the form appended. A service account has a username
  (`username-suffix` is shown after it); a person an email and names."
  [{:keys [tr scope kind target title groups-editor submit-event username-suffix help]}]
  (let [form    (<sub [::state/add-form scope])
        open?   (= [kind target] [(:kind form) (:target form)])
        person? (= :person kind)
        update! (fn [f & args]
                  (rf/dispatch-sync (into [::state/update-add-form scope f] args)))
        input   (fn [k id label-key & [props]]
                  [ui/text-input (merge {:id         id
                                         :label-text (tr [label-key])
                                         :value      (get form k "")
                                         :on-change  #(update! assoc k (-> % .-target .-value))}
                                        props)])
        valid?  (and (seq (:group-ids form))
                     (not (str/blank? (get form (if person? :email :username)))))
        close   #(>evt [::state/close-add-form scope])]
    [ui-modal/composed-input-modal
     {:open open? :on-close close}
     (cc/modal-header {:close-modal close :title title})
     (when open?
       (cc/modal-body
         {}
         [:div
          (when help (ui/help help))
          (if person?
            [:div.grid.gap-4 {:class "sm:grid-cols-3"}
             (input :email "um-add-email" :user/email {:type "email"})
             (input :first-name "um-add-first-name" :user/first-name)
             (input :last-name "um-add-last-name" :user/last-name)]
            [:div.flex.items-end.gap-2
             (input :username "um-add-username" :admin/tenant-service-account-username
                    {:helper-text (tr [:admin/tenant-service-account-username-help])})
             (when username-suffix [:span.mb-2.font-bold username-suffix])])
          (groups-editor (:group-ids form)
                         (fn [group-id checked?]
                           (update! update :group-ids (if checked? conj disj) group-id)))]))
     (cc/modal-footer
       {}
       (cc/button {:kind :secondary :on-click close} (tr [:cancel]))
       (cc/button {:kind     :primary
                   :disabled (not valid?)
                   :on-click #(do (>evt (conj submit-event form))
                                  (close))}
                  (tr [(if person? :admin/tenant-add-user :admin/tenant-add-service-account)])))]))

(defn new-api-key-modal
  "The secret of the scope's newly created API key, shown once."
  [{:keys [tr scope]}]
  (let [api-key (<sub [::state/new-api-key scope])
        close   #(>evt [::state/dismiss-new-api-key scope])]
    [ui-modal/composed-input-modal
     {:open (some? api-key) :on-close close}
     (cc/modal-header {:close-modal close
                       :title       (tr [:admin/tenant-api-key-created])})
     (when api-key
       (cc/modal-body
         {}
         [:div
          (ui/warning (tr [:admin/tenant-api-key-secret-warning]))
          [:div.mt-4
           [:div.bx--label (tr [:admin/tenant-api-key-client-id])]
           [:code.block.p-2.bg-ui-light-gray.rounded.break-all
            (:api-key/client-id api-key)]]
          [:div.mt-4
           [:div.bx--label (tr [:admin/tenant-api-key-client-secret])]
           [:code.block.p-2.bg-ui-light-gray.rounded.break-all
            (:api-key/client-secret api-key)]]]))
     (cc/modal-footer
       {}
       (cc/button {:kind :primary :on-click close} (tr [:close])))]))

;; ---------------------------------------------------------------------------
;; Search
;; ---------------------------------------------------------------------------

(defn search-field
  "`:light` (default true) for the purple tab background; false on a white section."
  [{:keys [tr scope id label light] :or {light true}}]
  (let [query (<sub [::state/query scope])]
    [:div.flex.items-center.gap-4.mb-4.flex-wrap
     [:div.flex-1.min-w-64
      [ui/search {:id                      id
                  :hide-label              true
                  :label-text              label
                  :placeholder             label
                  :light                   light
                  :value                   (or query "")
                  :clear-fn                #(>evt [::state/set-query scope ""])
                  :show-clear?             (boolean (seq query))
                  :close-button-label-text (tr [:clear])
                  :on-change               #(rf/dispatch-sync [::state/set-query scope (-> % .-target .-value)])}]]]))
